import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Flex, notification, Popconfirm, Row, Spin, Table, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/AuthProvider';
import { authAxios } from '../../services/auth-axios';
import { buildPagination, DEFAULT_PAGINATION_PARAMS } from '../shared/build-pagination';
import { TournamentModal } from './TournamentModal';
import dayjs from "dayjs";

export function Tournaments() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [paginationOptions, setPaginationOptions] = useState({ ...DEFAULT_PAGINATION_PARAMS });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [currentTournamentEditing, setCurrentTournamentEditing] = useState(false);
  const [formats, setFormats] = useState([]);
  const [games, setGames] = useState([]);

  const roles = localStorage.getItem('roles');
  const isAdmin = roles && roles.includes('ADMIN');

  const openAdd = () => setAddModalOpen(true);
  const closeAdd = () => setAddModalOpen(false);
  const closeEdit = () => setCurrentTournamentEditing(false);

  const fetchFormatsAndGames = async () => {
    try {
      const [formatsRes, gamesRes] = await Promise.all([
        authAxios().get('/formats', { params: { size: 1000 } }),
        authAxios().get('/games', { params: { size: 1000 } }),
      ]);
      setFormats(formatsRes.data.content);
      setGames(gamesRes.data.content);
    } catch (error) {
      console.error('Erro ao buscar formatos e jogos', error);
      notification.error({ message: 'Erro ao buscar formatos e jogos' });
    }
  };

  useEffect(() => {
    fetchFormatsAndGames();
  }, []);

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      const response = await authAxios().get('/tournaments', { params: paginationOptions });
      setTournaments(response.data);
    } catch (error) {
      notification.error({ message: 'Erro ao buscar torneios' });
      if (error.response?.status === 401) auth.logOut();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await authAxios().delete(`/tournaments/${id}`);
      await fetchTournaments();
      notification.success({ message: 'Torneio deletado com sucesso!' });
    } catch (error) {
      notification.error({ message: 'Erro ao deletar torneio' });
      if (error.response?.status === 401) auth.logOut();
    } finally {
      setLoading(false);
    }
  };

  const handlePaginate = (pageNumber, size) =>
    setPaginationOptions({
      ...paginationOptions,
      size,
      page: pageNumber - 1,
    });

  useEffect(() => {
    fetchTournaments();
  }, [paginationOptions]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Jogo',
      key: 'game',
      render: (_, tournament) => {
        let gameName = '';
        if (tournament.format && tournament.format.game && tournament.format.game.name) {
          gameName = tournament.format.game.name;
        } else if (tournament.format && tournament.format.id) {
          const foundFormat = formats.find((f) => f.id === tournament.format.id);
          if (foundFormat && foundFormat.game && foundFormat.game.name) {
            gameName = foundFormat.game.name;
          }
        }
        return gameName;
      },
    },
    {
      title: 'Formato',
      key: 'format',
      render: (_, tournament) => {
        let formatName = '';
        if (tournament.format && tournament.format.name) {
          formatName = tournament.format.name;
        } else if (tournament.format && tournament.format.id) {
          const foundFormat = formats.find((f) => f.id === tournament.format.id);
          if (foundFormat && foundFormat.name) {
            formatName = foundFormat.name;
          }
        }
        return formatName;
      },
    },
    {
      title: 'Data',
      dataIndex: 'dateTime',
      key: 'dateTime',
      render: (dateTime) => dayjs(dateTime).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusColors = {
          OPEN: 'green',
          CLOSED: 'red',
          PENDING: 'orange',
        };
        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, tournament) => (
        <Flex gap="small">
          <Button icon={<EyeOutlined />} type="primary" size="large" onClick={() => navigate(`/tournaments/${tournament.id}`)}/>
          {isAdmin && (
            <>
              <Popconfirm title="Deletar torneio?" onConfirm={() => handleDelete(tournament.id)}>
                <Button type="primary" danger icon={<DeleteOutlined />} size="large" />
              </Popconfirm>
              <TournamentModal
                tournament={tournament}
                isOpen={tournament.id === currentTournamentEditing}
                onFinish={closeEdit}
                onTournamentSave={fetchTournaments}
                operation="edit"
              >
                <Button icon={<EditOutlined />} size="large" onClick={() => setCurrentTournamentEditing(tournament.id)} />
              </TournamentModal>
            </>
          )}
        </Flex>
      ),
    },
  ];

  if (loading) return <Spin size="large" />;

  return (
    <Row gutter={[16, 16]}>
      {isAdmin && (
        <Col span={24}>
          <Card title="Ações" size="small">
            <TournamentModal isOpen={addModalOpen} onFinish={closeAdd} onTournamentSave={fetchTournaments} operation="create">
              <Button type="primary" onClick={openAdd}>
                Novo Torneio
              </Button>
            </TournamentModal>
          </Card>
        </Col>
      )}
      <Col span={24}>
        <Table
          rowKey={(record) => record.id}
          columns={columns}
          dataSource={tournaments.content}
          pagination={buildPagination(tournaments, handlePaginate)}
        />
      </Col>
    </Row>
  );
}
